/**
 * SpriteManager.ts
 * Sprite loading, caching, and animation system
 * Supports both file-based sprites and procedural fallbacks
 * Enhanced with high-quality procedural character and enemy sprites
 */

import { Sprite, SpriteSheet, AnimatedSprite } from './types/engine';
import { ProceduralSprites } from './ProceduralSprites';
import { getEnhancedProceduralSprites } from './EnhancedProceduralSprites';
import { EnhancedEnemySprites } from './EnhancedEnemySprites';
import { BattleCharacterSprites, BattlePose } from './BattleCharacterSprites';
import { ASSET_MANIFEST, AnimationDefinition } from './data/AssetManifest';

export class SpriteManager {
  private sheets: Map<string, SpriteSheet>;
  private sprites: Map<string, Sprite>;
  private canvasCache: Map<string, HTMLCanvasElement>;
  private loadingPromises: Map<string, Promise<void>>;
  private animations: Map<string, AnimatedSprite>;
  private proceduralSheetsGenerated: boolean = false;

  // Enhanced sprite generators
  private enhancedCharacterSprites: ReturnType<typeof getEnhancedProceduralSprites>;
  private enhancedEnemySprites: EnhancedEnemySprites;
  private battleCharacterSprites: BattleCharacterSprites;
  private proceduralSprites: ProceduralSprites;

  constructor() {
    this.sheets = new Map();
    this.sprites = new Map();
    this.canvasCache = new Map();
    this.loadingPromises = new Map();
    this.animations = new Map();

    // Initialize enhanced sprite generators
    this.enhancedCharacterSprites = getEnhancedProceduralSprites();
    this.enhancedEnemySprites = new EnhancedEnemySprites();
    this.battleCharacterSprites = new BattleCharacterSprites();
    this.proceduralSprites = new ProceduralSprites();
  }

  /**
   * Initialize with enhanced procedural sprites as immediate fallback
   */
  initializeWithProceduralSprites(): void {
    if (this.proceduralSheetsGenerated) return;

    console.log('[SpriteManager] Generating enhanced procedural sprites...');

    // Generate enhanced character sprites (12 classes x 12 frames)
    const characterCanvas = this.enhancedCharacterSprites.generateCharacters();
    this.canvasCache.set('characters', characterCanvas);
    this.registerCanvasAsSheet('characters', characterCanvas, 16, 16);

    // Generate enhanced enemy sprites (43 enemies)
    const enemyCanvas = this.enhancedEnemySprites.generateEnemies();
    this.canvasCache.set('enemies', enemyCanvas);
    this.registerCanvasAsSheet('enemies', enemyCanvas, 32, 32);

    // Generate tile and effect sprites from base procedural system
    const worldTileset = this.proceduralSprites.generateWorldTileset();
    const effects = this.proceduralSprites.generateEffects();
    this.canvasCache.set('world_tileset', worldTileset);
    this.canvasCache.set('effects', effects);
    this.registerCanvasAsSheet('world_tileset', worldTileset, 16, 16);
    this.registerCanvasAsSheet('effects', effects, 16, 16);

    // Generate 32x32 battle character sprites with poses
    const battleCharCanvas = this.battleCharacterSprites.generateBattleSprites();
    this.canvasCache.set('battle_characters', battleCharCanvas);
    this.registerCanvasAsSheet('battle_characters', battleCharCanvas, 32, 32);

    this.proceduralSheetsGenerated = true;
    console.log('[SpriteManager] Enhanced procedural sprites initialized');
    console.log('  - Characters: 12 classes x 12 frames (192x192)');
    console.log('  - Battle Characters: 6 classes x 6 poses (192x192)');
    console.log('  - Enemies: 43 unique sprites (256x256)');
    console.log('  - World tileset: terrain & structures (256x256)');
    console.log('  - Effects: battle animations (256x64)');
  }

  /**
   * Register a canvas element as a sprite sheet
   * Note: For procedural sprites, we store the canvas directly
   * This avoids async Image loading race conditions
   */
  registerCanvasAsSheet(
    name: string,
    canvas: HTMLCanvasElement,
    tileWidth: number,
    tileHeight: number
  ): void {
    const columns = Math.floor(canvas.width / tileWidth);
    const rows = Math.floor(canvas.height / tileHeight);

    // Store canvas directly - it's already in memory and ready to use
    // The SpriteSheet interface accepts HTMLCanvasElement as image source
    this.sheets.set(name, {
      image: canvas as unknown as HTMLImageElement, // Canvas is compatible for drawImage
      tileWidth,
      tileHeight,
      columns,
      rows,
    });
  }

  /**
   * Load a sprite sheet
   */
  async loadSheet(
    name: string,
    url: string,
    frameWidth: number,
    frameHeight: number
  ): Promise<void> {
    // Check if already loading
    if (this.loadingPromises.has(name)) {
      return this.loadingPromises.get(name);
    }

    // Check if already loaded
    if (this.sheets.has(name)) {
      return Promise.resolve();
    }

    const loadPromise = new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const columns = Math.floor(img.width / frameWidth);
        const rows = Math.floor(img.height / frameHeight);
        this.sheets.set(name, {
          image: img,
          tileWidth: frameWidth,
          tileHeight: frameHeight,
          columns,
          rows,
        });
        this.loadingPromises.delete(name);
        resolve();
      };
      img.onerror = () => {
        this.loadingPromises.delete(name);
        reject(new Error(`Failed to load sprite sheet: ${url}`));
      };
      img.src = url;
    });

    this.loadingPromises.set(name, loadPromise);
    return loadPromise;
  }

  /**
   * Get a single sprite from a sheet
   */
  getSprite(sheetName: string, frameIndex: number = 0): Sprite | null {
    const sheet = this.sheets.get(sheetName);
    if (!sheet) {
      console.warn(`Sprite sheet not found: ${sheetName}`);
      return null;
    }

    const cacheKey = `${sheetName}_${frameIndex}`;

    // Check cache
    if (this.sprites.has(cacheKey)) {
      return this.sprites.get(cacheKey)!;
    }

    // Calculate frame position
    const framesPerRow = sheet.columns;
    const row = Math.floor(frameIndex / framesPerRow);
    const col = frameIndex % framesPerRow;

    const sprite: Sprite = {
      id: cacheKey,
      image: sheet.image,
      x: col * sheet.tileWidth,
      y: row * sheet.tileHeight,
      width: sheet.tileWidth,
      height: sheet.tileHeight
    };

    // Cache it
    this.sprites.set(cacheKey, sprite);
    return sprite;
  }

  /**
   * Get animation frames
   */
  getAnimationFrames(sheetName: string, startFrame: number, frameCount: number): Sprite[] {
    const frames: Sprite[] = [];
    for (let i = 0; i < frameCount; i++) {
      const sprite = this.getSprite(sheetName, startFrame + i);
      if (sprite) {
        frames.push(sprite);
      }
    }
    return frames;
  }

  /**
   * Create a sprite from a subsection of an image
   */
  createSprite(name: string, image: HTMLImageElement, x: number, y: number, width: number, height: number): Sprite {
    const sprite: Sprite = { id: name, image, x, y, width, height };
    this.sprites.set(name, sprite);
    return sprite;
  }

  /**
   * Check if a sheet is loaded
   */
  isLoaded(name: string): boolean {
    return this.sheets.has(name);
  }

  /**
   * Preload multiple sheets
   */
  async preloadSheets(sheets: Array<{ name: string; url: string; frameWidth: number; frameHeight: number }>): Promise<void> {
    const promises = sheets.map(sheet =>
      this.loadSheet(sheet.name, sheet.url, sheet.frameWidth, sheet.frameHeight)
    );
    await Promise.all(promises);
  }

  /**
   * Clear all cached sprites (keeps sheets)
   */
  clearCache(): void {
    this.sprites.clear();
  }

  /**
   * Unload everything
   */
  unloadAll(): void {
    this.sheets.clear();
    this.sprites.clear();
    this.loadingPromises.clear();
    this.animations.clear();
  }

  /**
   * Create an animation from a definition
   */
  createAnimation(
    name: string,
    sheetName: string,
    definition: AnimationDefinition
  ): AnimatedSprite | null {
    const frames = definition.frames.map(frameIndex =>
      this.getSprite(sheetName, frameIndex)
    ).filter((s): s is Sprite => s !== null);

    if (frames.length === 0) return null;

    const animation: AnimatedSprite = {
      frames,
      frameDuration: definition.frameTime,
      loop: definition.loop ?? true,
      currentFrame: 0,
      elapsed: 0,
    };

    this.animations.set(name, animation);
    return animation;
  }

  /**
   * Get animation by name
   */
  getAnimation(name: string): AnimatedSprite | null {
    return this.animations.get(name) ?? null;
  }

  /**
   * Update an animation and get current frame
   */
  updateAnimation(animation: AnimatedSprite, deltaTime: number): Sprite {
    animation.elapsed += deltaTime;

    if (animation.elapsed >= animation.frameDuration) {
      animation.elapsed = 0;
      animation.currentFrame++;

      if (animation.currentFrame >= animation.frames.length) {
        animation.currentFrame = animation.loop ? 0 : animation.frames.length - 1;
      }
    }

    const frame = animation.frames[animation.currentFrame] ?? animation.frames[0];
    if (!frame) {
      throw new Error('Animation has no frames');
    }
    return frame;
  }

  /**
   * Get frame from animation based on time
   */
  getAnimationFrame(sheetName: string, frames: number[], time: number, frameTime: number): Sprite | null {
    if (frames.length === 0) return null;

    const frameIndex = Math.floor(time / frameTime) % frames.length;
    return this.getSprite(sheetName, frames[frameIndex]);
  }

  /**
   * Load all assets from the manifest
   */
  async loadFromManifest(): Promise<void> {
    // First initialize procedural sprites as fallback
    this.initializeWithProceduralSprites();

    // Then try to load actual sprite files (will override procedural if successful)
    const loadPromises = ASSET_MANIFEST.spritesheets.map(async (sheet) => {
      try {
        await this.loadSheet(sheet.name, sheet.url, sheet.frameWidth, sheet.frameHeight);
        console.log(`[SpriteManager] Loaded: ${sheet.name}`);
      } catch (error) {
        console.log(`[SpriteManager] Using procedural fallback for: ${sheet.name}`);
        // Procedural fallback already loaded, so this is fine
      }
    });

    await Promise.allSettled(loadPromises);
  }

  // =========================================================================
  // Enhanced Character Sprite Methods
  // =========================================================================

  /**
   * Get character sprite by class name and frame
   * @param characterClass - Class name (FIGHTER, THIEF, MONK, etc.)
   * @param frameIndex - Frame index within that class (0-11)
   * @returns Sprite or null if not found
   */
  getCharacterSprite(characterClass: string, frameIndex: number = 0): Sprite | null {
    const classRow = ASSET_MANIFEST.characterClassRows[characterClass];
    if (classRow === undefined) {
      console.warn(`[SpriteManager] Unknown character class: ${characterClass}`);
      return null;
    }

    // Calculate absolute frame index: row * 12 + frame
    const absoluteFrame = classRow * 12 + frameIndex;
    return this.getSprite('characters', absoluteFrame);
  }

  /**
   * Get character sprite for a specific direction and walk frame
   * @param characterClass - Class name
   * @param direction - 0=down, 1=up, 2=left, 3=right
   * @param walkFrame - Walk animation frame (0-2)
   */
  getCharacterSpriteByDirection(
    characterClass: string,
    direction: number,
    walkFrame: number = 0
  ): Sprite | null {
    // Each direction has 3 frames: direction * 3 + walkFrame
    const frameIndex = direction * 3 + (walkFrame % 3);
    return this.getCharacterSprite(characterClass, frameIndex);
  }

  /**
   * Get all 12 frames for a character class
   */
  getCharacterFrames(characterClass: string): Sprite[] {
    const frames: Sprite[] = [];
    for (let i = 0; i < 12; i++) {
      const sprite = this.getCharacterSprite(characterClass, i);
      if (sprite) frames.push(sprite);
    }
    return frames;
  }

  // =========================================================================
  // Battle Character Sprite Methods (32x32 with poses)
  // =========================================================================

  /**
   * Get battle character sprite by class name and pose
   * These are larger 32x32 sprites designed for battle display
   * @param characterClass - Class name (FIGHTER, THIEF, MONK, RED_MAGE, WHITE_MAGE, BLACK_MAGE)
   * @param pose - BattlePose enum (IDLE, ATTACK, CAST, HURT, VICTORY, DEAD)
   * @returns Sprite or null if not found
   */
  getBattleCharacterSprite(characterClass: string, pose: BattlePose = BattlePose.IDLE): Sprite | null {
    const classIndex = ['FIGHTER', 'THIEF', 'MONK', 'RED_MAGE', 'WHITE_MAGE', 'BLACK_MAGE'].indexOf(characterClass);
    if (classIndex === -1) {
      console.warn(`[SpriteManager] Unknown battle character class: ${characterClass}`);
      return null;
    }

    // Layout: 6 poses per row (x), 6 classes per column (y)
    // Frame index = classIndex * 6 + poseIndex
    const frameIndex = classIndex * 6 + pose;
    return this.getSprite('battle_characters', frameIndex);
  }

  /**
   * Get all battle poses for a character class
   */
  getBattleCharacterPoses(characterClass: string): Map<BattlePose, Sprite | null> {
    const poses = new Map<BattlePose, Sprite | null>();
    for (let pose = 0; pose < 6; pose++) {
      poses.set(pose as BattlePose, this.getBattleCharacterSprite(characterClass, pose as BattlePose));
    }
    return poses;
  }

  // =========================================================================
  // Enhanced Enemy Sprite Methods
  // =========================================================================

  /**
   * Get enemy sprite by enemy ID (1-43)
   * @param enemyId - Enemy ID from EnemyData.sol (1-43)
   * @returns Sprite or null if not found
   */
  getEnemySprite(enemyId: number): Sprite | null {
    if (enemyId < 1 || enemyId > 43) {
      console.warn(`[SpriteManager] Enemy ID out of range: ${enemyId} (expected 1-43)`);
      return null;
    }

    // Enemy sprites are laid out in 8x8 grid (0-indexed)
    // Enemy ID 1 = frame 0, Enemy ID 43 = frame 42
    const frameIndex = enemyId - 1;
    return this.getSprite('enemies', frameIndex);
  }

  /**
   * Get enemy sprite position in the sprite sheet
   * Useful for rendering at different scales
   */
  getEnemySpriteInfo(enemyId: number): { x: number; y: number; size: number } | null {
    if (enemyId < 1 || enemyId > 43) return null;

    const frameIndex = enemyId - 1;
    const col = frameIndex % 8;
    const row = Math.floor(frameIndex / 8);

    return {
      x: col * 32,
      y: row * 32,
      size: 32, // Base size, can be scaled up for large enemies
    };
  }

  // =========================================================================
  // Sprite Scaling and Canvas Utilities
  // =========================================================================

  /**
   * Create a scaled version of a sprite
   * @param sprite - Source sprite
   * @param scale - Scale factor (2 = double size, 3 = triple, etc.)
   * @returns New scaled sprite
   */
  createScaledSprite(sprite: Sprite, scale: number): Sprite {
    const cacheKey = `${sprite.id}_scale${scale}`;

    // Check if already cached
    const cached = this.sprites.get(cacheKey);
    if (cached) return cached;

    // Create scaled canvas
    const canvas = document.createElement('canvas');
    canvas.width = sprite.width * scale;
    canvas.height = sprite.height * scale;
    const ctx = canvas.getContext('2d')!;

    // Disable image smoothing for pixel-perfect scaling
    ctx.imageSmoothingEnabled = false;

    // Draw scaled sprite
    ctx.drawImage(
      sprite.image,
      sprite.x,
      sprite.y,
      sprite.width,
      sprite.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Use canvas directly to avoid async Image loading race condition
    const scaledSprite: Sprite = {
      id: cacheKey,
      image: canvas as unknown as HTMLImageElement, // Canvas is compatible for drawImage
      x: 0,
      y: 0,
      width: canvas.width,
      height: canvas.height,
    };

    this.sprites.set(cacheKey, scaledSprite);
    return scaledSprite;
  }

  /**
   * Get a sprite at a specific scale
   */
  getSpriteScaled(sheetName: string, frameIndex: number, scale: number): Sprite | null {
    const sprite = this.getSprite(sheetName, frameIndex);
    if (!sprite) return null;
    if (scale === 1) return sprite;
    return this.createScaledSprite(sprite, scale);
  }

  /**
   * Get the raw canvas for a sprite sheet (useful for debugging/export)
   */
  getCanvasForSheet(sheetName: string): HTMLCanvasElement | null {
    return this.canvasCache.get(sheetName) ?? null;
  }

  // =========================================================================
  // UI and Effect Sprite Methods
  // =========================================================================

  /**
   * Get a tile sprite (for world map rendering)
   * Supports both simple and animated tile lookups
   */
  getTileSprite(
    sheetNameOrTileId: string | number,
    tileId?: number,
    animFrames?: number[],
    time?: number,
    frameTime?: number
  ): Sprite | null {
    // Handle legacy single-parameter call: getTileSprite(tileId)
    if (typeof sheetNameOrTileId === 'number' && tileId === undefined) {
      return this.getSprite('world_tileset', sheetNameOrTileId);
    }

    // Handle new multi-parameter call: getTileSprite(sheetName, tileId, ...)
    const sheetName = typeof sheetNameOrTileId === 'string' ? sheetNameOrTileId : 'world_tileset';
    const actualTileId = tileId ?? (typeof sheetNameOrTileId === 'number' ? sheetNameOrTileId : 0);

    if (animFrames && animFrames.length > 0 && time !== undefined) {
      const ft = frameTime ?? 300;
      const frameIndex = Math.floor(time / ft) % animFrames.length;
      return this.getSprite(sheetName, animFrames[frameIndex]);
    }
    return this.getSprite(sheetName, actualTileId);
  }

  /**
   * Get an effect sprite (for battle animations)
   */
  getEffectSprite(effectId: number): Sprite | null {
    return this.getSprite('effects', effectId);
  }

  /**
   * Get effect animation frames
   */
  getEffectAnimation(effectName: string): Sprite[] {
    const animation = ASSET_MANIFEST.battleEffects[effectName];
    if (!animation) {
      console.warn(`[SpriteManager] Unknown effect: ${effectName}`);
      return [];
    }

    return animation.frames
      .map(frameIndex => this.getSprite('effects', frameIndex))
      .filter((s): s is Sprite => s !== null);
  }

  // =========================================================================
  // Debug and Utility Methods
  // =========================================================================

  /**
   * Get statistics about loaded sprites
   */
  getStats(): {
    sheetsLoaded: number;
    spritesCached: number;
    canvasesCached: number;
    animations: number;
  } {
    return {
      sheetsLoaded: this.sheets.size,
      spritesCached: this.sprites.size,
      canvasesCached: this.canvasCache.size,
      animations: this.animations.size,
    };
  }

  /**
   * Verify all character classes are available
   */
  verifyCharacterSprites(): boolean {
    const classes = Object.keys(ASSET_MANIFEST.characterClassRows);
    let allValid = true;

    console.log('[SpriteManager] Verifying character sprites...');
    for (const className of classes) {
      const sprite = this.getCharacterSprite(className, 0);
      if (!sprite) {
        console.error(`  ✗ ${className}: MISSING`);
        allValid = false;
      } else {
        console.log(`  ✓ ${className}: OK`);
      }
    }

    return allValid;
  }

  /**
   * Verify all enemy sprites are available
   */
  verifyEnemySprites(): boolean {
    console.log('[SpriteManager] Verifying enemy sprites...');
    let allValid = true;

    for (let i = 1; i <= 43; i++) {
      const sprite = this.getEnemySprite(i);
      if (!sprite) {
        console.error(`  ✗ Enemy ${i}: MISSING`);
        allValid = false;
      }
    }

    if (allValid) {
      console.log(`  ✓ All 43 enemies: OK`);
    }

    return allValid;
  }
}
